import { watch, type Ref } from 'vue'
import { ActionTypeEnum, EventNameEnum, type TAgentAction } from '@/types/generated'
import type { CustomerCase as Case } from '@/types/generated'
import { useMetrics } from '@/composables/useMetrics'
import type { AiAssistantWidgetOptions } from '@/composables/useAiAssistantWidget'

/**
 * Client-only AI Assistant analytics for the case-detail widget (no authoritative backend request).
 *
 * Draft confirm/discard and thumbs feedback are tracked in Django (see ``ai_assistant_event_tracking``).
 */
export function useAiAssistantCaseDetailMetrics(caseRecord: Ref<Case | undefined | null>) {
  const { trackEvent } = useMetrics()

  const widgetAnalyticsOptions: Pick<AiAssistantWidgetOptions, 'onReviewScreenEntered'> = {
    onReviewScreenEntered: (action: TAgentAction | null) => {
      if (action?.action_type !== ActionTypeEnum.SEND_CLIENT_TEMPLATE_EMAIL) {
        return
      }
      const case_uuid = caseRecord.value?.uuid
      if (!case_uuid) {
        return
      }
      void trackEvent(EventNameEnum.AI_ASSISTANT_DRAFT_REVIEW_NAVIGATED, { case_uuid })
    }
  }

  return { widgetAnalyticsOptions, trackEvent }
}

/**
 * Fire ``ai_assistant.floating_view_opened`` when the widget opens (not on subsequent step changes).
 *
 * :param isOpen: Widget open flag
 * :param getHadPendingActions: Pending-actions flag at open time (from widget composable)
 * :param caseRecord: Current case
 * :param trackEvent: Metrics ``trackEvent`` from ``useAiAssistantCaseDetailMetrics``
 */
export function watchAiAssistantFloatingViewOpened(
  isOpen: Ref<boolean>,
  getHadPendingActions: () => boolean,
  caseRecord: Ref<Case | undefined | null>,
  trackEvent: ReturnType<typeof useMetrics>['trackEvent']
) {
  watch(isOpen, (open, wasOpen) => {
    if (!open || wasOpen) {
      return
    }
    const case_uuid = caseRecord.value?.uuid
    if (!case_uuid) {
      return
    }
    void trackEvent(EventNameEnum.AI_ASSISTANT_FLOATING_VIEW_OPENED, {
      case_uuid,
      had_pending_actions: getHadPendingActions()
    })
  })
}
